import { useNavigate, useParams } from "react-router-dom";
import { Warehouse, Package, ChevronRight, QrCode } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAllStorageBins, useStorageLocations } from "@/hooks/useStorageBins";
import { useSparePartsByBin, type SparePartRow } from "@/hooks/useSpareParts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { isTakenOut } from "@/lib/partStatus";
import { binLabelFooter, binLevelEntries } from "@/lib/binLevels";
import { StorageBinLabelPreview } from "@/components/labels/StorageBinLabelPreview";
import { SparePartLabelPrintMenu } from "@/components/labels/SparePartLabelPrintMenu";
import { storageBinToLabelData } from "@/labels/storageBinJobs";
import {
  resolveSparePartLabelSize,
  useLabelPrinter,
  useLabelPrintingSettings,
} from "@/labels";

function statusVariant(status: string): "default" | "secondary" | "outline" {
  if (status === "stored") return "default";
  if (status === "pending_placement") return "secondary";
  return "outline";
}

/** FIX-298: one status vocabulary on all inventory pages. */
function statusKey(status: string): string {
  if (status === "stored") return "status.inStock";
  if (status === "pending_placement") return "status.notInBin";
  if (status === "damaged") return "status.damaged";
  if (isTakenOut(status)) return "status.takenOut";
  return "status.notInBin";
}

const LEVEL_LABELS = { aisle: "Aisle", bay: "Bay", level: "Level", shelf: "Shelf", bin: "Bin" } as const;

export default function BinDetail() {
  const { t } = useTranslation("inventory");
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: bins = [] } = useAllStorageBins();
  const { data: locations = [] } = useStorageLocations();
  const { data: parts = [] } = useSparePartsByBin(id);

  const bin = bins.find((b) => b.id === id);
  const location = bin ? locations.find((l) => l.id === bin.location_id) : null;
  const address = bin?.address || id || "";

  const updatedAt = bin?.updated_at || bin?.created_at || null;

  const handlePrint = () => {
    window.print();
  };

  const { settings: labelPrintingSettings } = useLabelPrintingSettings();
  const { printJobs } = useLabelPrinter();
  // One label size and one set of label data for Zebra, browser print and the preview.
  const size = resolveSparePartLabelSize(labelPrintingSettings.defaultLabelSize);
  const labelData = bin
    ? storageBinToLabelData(bin, { locationName: location?.name, footer: binLabelFooter(t, bin) })
    : null;
  const handleZebraPrint = async () => {
    if (!labelData) return;
    await printJobs([
      {
        kind: "storage-bin",
        data: labelData,
        options: { size },
      },
    ]);
  };

  const goToPart = (p: SparePartRow) => {
    const token = p.serial_no || p.qr_token;
    if (token) navigate(`/spare-part/${token}`);
  };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #bin-print-area, #bin-print-area * { visibility: visible !important; }
          #bin-print-area { position: fixed; inset: 0; padding: 40px; background: white; }
        }
        @media screen { #bin-print-area { display: none; } }
      `}</style>

      <div className="min-h-screen bg-background pb-24">
        <div className="px-6 pt-8 pb-6">
          <nav className="text-xs text-muted-foreground mb-6">
            <button onClick={() => navigate("/inventory?tab=bins")} className="hover:text-foreground">
              Inventory
            </button>
            <span className="mx-1.5">/</span>
            <span className="text-foreground font-mono">{address}</span>
          </nav>

          <div className="flex items-center gap-3 mb-2">
            <Warehouse className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-foreground font-mono">{address}</h1>
          </div>
          {location && <p className="text-muted-foreground">{location.name}</p>}
          {bin?.description && (
            <p className="text-sm text-foreground mt-1">{bin.description}</p>
          )}
        </div>

        <div className="px-6 grid gap-6 md:grid-cols-2">
          {/* Bin Details */}
          <div className="bg-card rounded-xl border border-border p-6">
            <h2 className="text-sm font-semibold text-foreground mb-4">Bin Details</h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Location</dt>
                <dd className="text-foreground text-right">{location?.name || "—"}</dd>
              </div>
              {/* Only the levels this location uses (FIX-318). */}
              {(bin ? binLevelEntries(bin) : [{ key: "bin" as const, value: null }]).map(({ key, value }) => (
                <div key={key} className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{LEVEL_LABELS[key]}</dt>
                  <dd className="text-foreground">{value ?? "—"}</dd>
                </div>
              ))}
              <div className="flex justify-between gap-4 pt-3 border-t border-border">
                <dt className="text-muted-foreground">Bin code</dt>
                <dd className="text-foreground font-mono">{address}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Description</dt>
                <dd className="text-foreground text-right">{bin?.description || "—"}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Updated</dt>
                <dd className="text-foreground">
                  {updatedAt ? format(new Date(updatedAt), "dd MMM, yyyy") : "—"}
                </dd>
              </div>
            </dl>
          </div>

          {/* QR label */}
          <div className="bg-card rounded-xl border border-border p-6 flex flex-col items-center">
            <div className="w-full flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground inline-flex items-center gap-2">
                <QrCode className="w-4 h-4" />
                Bin QR Code Label
              </h2>
              <SparePartLabelPrintMenu
                namespace="inventory"
                mode="single"
                iconOnly
                ariaLabel="Print label"
                onPrintBrowser={handlePrint}
                onPrintZebra={handleZebraPrint}
              />
            </div>
            {labelData ? <StorageBinLabelPreview data={labelData} size={size} /> : null}
          </div>
        </div>

        {/* Parts list */}
        <div className="px-6 mt-8">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-sm font-semibold text-foreground">Parts in this bin</h2>
            <span className="text-xs text-muted-foreground">{parts.length} total</span>
          </div>

          {parts.length === 0 ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center text-muted-foreground text-sm">
              No parts currently stored in this bin.
            </div>
          ) : (
            <ul className="space-y-2">
              {parts.map((p) => (
                <li
                  key={p.id}
                  onClick={() => goToPart(p)}
                  className="group bg-card rounded-xl border border-border p-4 flex items-center gap-4 cursor-pointer hover:border-primary/40 hover:bg-accent/30 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground truncate">{p.name}</span>
                      {p.serial_no && (
                        <span className="font-mono text-xs text-muted-foreground">{p.serial_no}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {[p.brand, p.model_number, p.parts_category_name].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  <Badge variant={statusVariant(p.status)} className="flex-shrink-0">
                    {t(statusKey(p.status))}
                  </Badge>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0" />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Print-only area */}
      <div id="bin-print-area">
        {labelData ? <StorageBinLabelPreview data={labelData} size={size} showSize={false} /> : null}
      </div>
    </>
  );
}
